import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError, fileUrl } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Upload, Trash2, Plus, X } from "lucide-react";
import { PosterBox, BudgetBar } from "@/components/brand";

const STEPS = ["Basics", "Access", "Payout & budget", "Requirements", "Posters", "Publish"];
const PLATFORMS = [["youtube", "Shorts"], ["instagram", "Reels"]];
const HERO_PRESETS = [["1920", "720"], ["1600", "600"], ["1920", "1080"], ["1200", "630"], ["1080", "1350"]];
const CARD_PRESETS = [["800", "450"], ["600", "600"], ["720", "900"], ["1080", "1920"]];

const blank = () => ({
  title: "", slug: "", brand_name: "", category: "general", tagline: "", description: "",
  status: "draft", visibility: "public", join_mode: "instant",
  platforms: PLATFORMS.map(([p, l]) => ({ platform: p, label: l, enabled: p === "youtube", rate_per_1k: 150, min_payout: 100, max_payout: 5000 })),
  currency: "INR", budget_total: 10000, per_clipper_cap: null, participant_cap: null,
  tracking_window_days: 30, payout_hold_days: 7, min_views: 0,
  requirements: { hashtags: [], mentions: [], min_duration: 15, max_duration: 90, max_per_day: 5 },
  rules: [], references: [], auto_approve: false, end_at: "",
  featured_in_hero: false, hero_order: 0, hero_has_text: false,
});

export default function CampaignForm({ open, onClose, campaignId }) {
  const [step, setStep] = useState(0);
  const [c, setC] = useState(blank());
  const [id, setId] = useState(campaignId || null);
  const [codes, setCodes] = useState([]);
  const [allow, setAllow] = useState([]);

  useEffect(() => {
    if (open && campaignId) {
      api.get(`/admin/campaigns/${campaignId}`).then((r) => { setC({ ...blank(), ...r.data, hero_has_text: r.data.posters?.hero_has_text }); setId(campaignId); setCodes(r.data.invite_codes || []); setAllow(r.data.allowlist || []); });
    } else if (open) { setC(blank()); setId(null); setStep(0); setCodes([]); setAllow([]); }
  }, [open, campaignId]);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const setReq = (k, v) => setC((p) => ({ ...p, requirements: { ...p.requirements, [k]: v } }));

  const saveDraft = async (publish = false) => {
    const payload = { ...c };
    if (publish) payload.status = "active";
    try {
      let res;
      if (id) res = await api.put(`/admin/campaigns/${id}`, payload);
      else { res = await api.post("/admin/campaigns", payload); setId(res.data.id); }
      toast.success(publish ? "Campaign published" : "Saved");
      if (publish) onClose();
      return res.data.id || id;
    } catch (e) { toast.error(apiError(e)); }
  };

  const togglePlatform = (i) => { const p = [...c.platforms]; p[i].enabled = !p[i].enabled; set("platforms", p); };
  const setPlat = (i, k, v) => { const p = [...c.platforms]; p[i][k] = v; set("platforms", p); };

  const addCode = async () => { if (!id) { toast.error("Save the campaign first"); return; } const { data } = await api.post(`/admin/campaigns/${id}/invite-codes`, { label: "Code" }); setCodes([...codes, data]); };
  const revoke = async (cid) => { await api.delete(`/admin/invite-codes/${cid}`); setCodes(codes.map((x) => x.id === cid ? { ...x, revoked: true } : x)); };
  const addAllow = async (v) => { const { data } = await api.post(`/admin/campaigns/${id}/allowlist`, { value: v }); setAllow([...allow, data]); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent style={{ maxWidth: 720, background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--text)", borderRadius: 20, maxHeight: "92vh", overflowY: "auto" }} data-testid="campaign-form">
        <DialogHeader><DialogTitle style={{ fontWeight: 500 }}>{id ? "Edit campaign" : "Add campaign"}</DialogTitle></DialogHeader>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => setStep(i)} data-testid={`step-${i}`} style={{ padding: "5px 10px", borderRadius: 999, fontSize: 12, cursor: "pointer", border: "1px solid " + (step === i ? "var(--accent)" : "var(--border)"), background: step === i ? "rgba(42,79,214,.15)" : "transparent", color: step === i ? "var(--accent-light)" : "var(--muted)" }}>{i + 1}. {s}</button>
          ))}
        </div>

        {step === 0 && (
          <Fields>
            <F label="Title"><input className="input-dark" value={c.title} onChange={(e) => set("title", e.target.value)} data-testid="cf-title" /></F>
            <F label="Slug (auto)"><input className="input-dark" value={c.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto-generated" /></F>
            <F label="Brand name"><input className="input-dark" value={c.brand_name} onChange={(e) => set("brand_name", e.target.value)} data-testid="cf-brand" /></F>
            <F label="Category"><input className="input-dark" value={c.category} onChange={(e) => set("category", e.target.value)} /></F>
            <F label="Tagline (hero headline)"><input className="input-dark" value={c.tagline} onChange={(e) => set("tagline", e.target.value)} /></F>
            <F label="Full description"><textarea className="input-dark" rows={4} value={c.description} onChange={(e) => set("description", e.target.value)} /></F>
            <F label="Status"><select className="input-dark" value={c.status} onChange={(e) => set("status", e.target.value)}><option value="draft">Draft</option><option value="active">Active</option><option value="paused">Paused</option><option value="ended">Ended</option></select></F>
          </Fields>
        )}

        {step === 1 && (
          <Fields>
            <F label="Visibility"><select className="input-dark" value={c.visibility} onChange={(e) => set("visibility", e.target.value)} data-testid="cf-visibility"><option value="public">Public</option><option value="private">Private</option></select></F>
            {c.visibility === "public" && <F label="Join mode"><select className="input-dark" value={c.join_mode} onChange={(e) => set("join_mode", e.target.value)}><option value="instant">Instant join</option><option value="approval">Admin approval</option></select></F>}
            <F label="Participant cap (optional)"><input className="input-dark" type="number" value={c.participant_cap || ""} onChange={(e) => set("participant_cap", e.target.value ? +e.target.value : null)} /></F>
            {c.visibility === "private" && (
              <div style={{ gridColumn: "1/-1" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: "var(--sub)" }}>Invite codes</span>
                  <button className="btn-outline focus-ring" onClick={addCode} style={{ padding: "6px 12px", fontSize: 12 }}><Plus size={12} /> Generate code</button>
                </div>
                {codes.map((x) => (
                  <div key={x.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid var(--border)", fontSize: 13, opacity: x.revoked ? .4 : 1 }}>
                    <code style={{ color: "var(--accent-light)" }}>{x.code}</code>
                    <span style={{ color: "var(--muted)" }}>uses {x.uses}{x.max_uses ? `/${x.max_uses}` : ""}</span>
                    {!x.revoked && <button onClick={() => revoke(x.id)} style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", fontSize: 12 }}>Revoke</button>}
                  </div>
                ))}
                <AllowInput onAdd={addAllow} items={allow} />
              </div>
            )}
          </Fields>
        )}

        {step === 2 && (
          <div>
            <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              <F label="Currency"><input className="input-dark" value={c.currency} onChange={(e) => set("currency", e.target.value)} /></F>
              <F label="Total budget"><input className="input-dark" type="number" value={c.budget_total} onChange={(e) => set("budget_total", +e.target.value)} data-testid="cf-budget" /></F>
            </div>
            {c.platforms.map((p, i) => (
              <div key={p.platform} className="raised" style={{ borderRadius: 10, padding: 12, marginBottom: 8 }}>
                <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: p.enabled ? 10 : 0 }}>
                  <input type="checkbox" checked={p.enabled} onChange={() => togglePlatform(i)} data-testid={`cf-plat-${p.platform}`} /> <b style={{ fontSize: 13 }}>{p.label} ({p.platform})</b>
                </label>
                {p.enabled && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
                    <F label="Rate / 1K"><input className="input-dark" type="number" value={p.rate_per_1k} onChange={(e) => setPlat(i, "rate_per_1k", +e.target.value)} /></F>
                    <F label="Max payout / clip"><input className="input-dark" type="number" value={p.max_payout} onChange={(e) => setPlat(i, "max_payout", +e.target.value)} /></F>
                  </div>
                )}
              </div>
            ))}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12, marginTop: 10 }}>
              <F label="Per-clipper cap"><input className="input-dark" type="number" value={c.per_clipper_cap || ""} onChange={(e) => set("per_clipper_cap", e.target.value ? +e.target.value : null)} /></F>
              <F label="Track views for (days)"><input className="input-dark" type="number" value={c.tracking_window_days} onChange={(e) => set("tracking_window_days", +e.target.value)} /></F>
            </div>
          </div>
        )}

        {step === 3 && (
          <Fields>
            <F label="Required hashtags (comma sep)"><input className="input-dark" value={c.requirements.hashtags.join(", ")} onChange={(e) => setReq("hashtags", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></F>
            <F label="Required mentions (comma sep)"><input className="input-dark" value={c.requirements.mentions.join(", ")} onChange={(e) => setReq("mentions", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></F>
            <F label="Min duration (s)"><input className="input-dark" type="number" value={c.requirements.min_duration} onChange={(e) => setReq("min_duration", +e.target.value)} /></F>
            <F label="Max duration (s)"><input className="input-dark" type="number" value={c.requirements.max_duration} onChange={(e) => setReq("max_duration", +e.target.value)} /></F>
            <F label="Max submissions / day"><input className="input-dark" type="number" value={c.requirements.max_per_day} onChange={(e) => setReq("max_per_day", +e.target.value)} /></F>
            <F label="Rules (one per line)"><textarea className="input-dark" rows={3} value={c.rules.join("\n")} onChange={(e) => set("rules", e.target.value.split("\n").filter(Boolean))} /></F>
            <F label="Reference links (label|url per line)"><textarea className="input-dark" rows={3} value={c.references.map((r) => `${r.label}|${r.url}`).join("\n")} onChange={(e) => set("references", e.target.value.split("\n").filter(Boolean).map((l) => { const [label, url] = l.split("|"); return { label: label?.trim(), url: url?.trim() }; }))} /></F>
            <F label="Auto-approve"><label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}><input type="checkbox" checked={c.auto_approve} onChange={(e) => set("auto_approve", e.target.checked)} /> Approve submissions automatically</label></F>
          </Fields>
        )}

        {step === 4 && <PosterStep c={c} set={set} id={id} saveDraft={saveDraft} reload={() => api.get(`/admin/campaigns/${id}`).then((r) => setC({ ...c, posters: r.data.posters }))} />}

        {step === 5 && (
          <div>
            <div style={{ fontSize: 13, color: "var(--sub)", marginBottom: 12 }}>Preview</div>
            <PosterBox poster={c.posters?.hero} style={{ height: 160, marginBottom: 10 }}><div className="scrim-left" style={{ position: "absolute", inset: 0 }} /><div style={{ position: "absolute", bottom: 14, left: 16 }}><div style={{ color: "#fff", fontSize: 18 }}>{c.tagline || c.title}</div><div style={{ color: "var(--banner-sub)", fontSize: 12 }}>{c.brand_name}</div></div></PosterBox>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginBottom: 10 }}><input type="checkbox" checked={c.featured_in_hero} onChange={(e) => set("featured_in_hero", e.target.checked)} data-testid="cf-hero" /> Show in hero banner</label>
            <F label="Hero order"><input className="input-dark" type="number" value={c.hero_order} onChange={(e) => set("hero_order", +e.target.value)} style={{ width: 100 }} /></F>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, gap: 8 }}>
          <button className="btn-outline focus-ring" onClick={() => saveDraft(false)} data-testid="cf-save-draft" style={{ padding: "10px 16px" }}>Save draft</button>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && <button className="btn-outline focus-ring" onClick={() => setStep(step - 1)} style={{ padding: "10px 16px" }}>Back</button>}
            {step < 5 ? <button className="btn-brand focus-ring" onClick={() => setStep(step + 1)} data-testid="cf-next" style={{ padding: "10px 20px" }}>Next</button>
              : <button className="btn-brand focus-ring" onClick={() => saveDraft(true)} data-testid="cf-publish" style={{ padding: "10px 20px" }}>Publish</button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Fields({ children }) { return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>; }
function F({ label, children }) { return <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 12, color: "var(--muted)" }}>{label}</label>{children}</div>; }

function AllowInput({ onAdd, items }) {
  const [v, setV] = useState("");
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 13, color: "var(--sub)", marginBottom: 8 }}>Allowlist (email or username)</div>
      <div style={{ display: "flex", gap: 8 }}>
        <input className="input-dark" value={v} onChange={(e) => setV(e.target.value)} placeholder="email or @username" />
        <button className="btn-outline focus-ring" onClick={() => { onAdd(v); setV(""); }} style={{ padding: "0 14px" }}>Add</button>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>{items.map((a) => <span key={a.id} className="chip">{a.value}</span>)}</div>
    </div>
  );
}

function PosterStep({ c, set, id, saveDraft, reload }) {
  return (
    <div>
      {!id && <div style={{ fontSize: 13, color: "var(--warning)", marginBottom: 10 }}>Save the campaign first to upload posters. <button className="btn-outline" onClick={() => saveDraft(false)} style={{ padding: "4px 10px", fontSize: 12, marginLeft: 8 }}>Save draft</button></div>}
      <PosterSlot slot="hero" label="Hero poster (banner)" presets={HERO_PRESETS} id={id} poster={c.posters?.hero} reload={reload} campaign={c} set={set} />
      <PosterSlot slot="card" label="Card poster (grid)" presets={CARD_PRESETS} id={id} poster={c.posters?.card} reload={reload} />
    </div>
  );
}

function PosterSlot({ slot, label, presets, id, poster, reload, campaign, set }) {
  const fileRef = useRef();
  const [preset, setPreset] = useState(presets[0]);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!id) { toast.error("Save the campaign first"); return; }
    const fd = new FormData();
    fd.append("slot", slot); fd.append("width", preset[0]); fd.append("height", preset[1]);
    fd.append("focal_x", "0.5"); fd.append("focal_y", "0.4"); fd.append("file", file);
    if (slot === "hero" && campaign) fd.append("has_text", String(!!campaign.hero_has_text));
    setBusy(true);
    try { const { data } = await api.post(`/admin/campaigns/${id}/poster`, fd, { headers: { "Content-Type": "multipart/form-data" } }); if (data.low_quality) toast.warning("Uploaded image is smaller than target — may look low quality"); else toast.success("Poster uploaded"); reload(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const remove = async () => { await api.delete(`/admin/campaigns/${id}/poster/${slot}`); reload(); };
  const showOverlayText = slot === "hero" && !(campaign?.hero_has_text);
  return (
    <div className="raised" style={{ borderRadius: 12, padding: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <b style={{ fontSize: 13 }}>{label}</b>
        <select className="input-dark" value={preset.join("x")} onChange={(e) => setPreset(e.target.value.split("x"))} style={{ width: "auto", fontSize: 12, padding: "6px 8px" }}>
          {presets.map((p) => <option key={p.join("x")} value={p.join("x")}>{p[0]}×{p[1]}</option>)}
        </select>
      </div>
      {slot === "hero" && (
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginBottom: 10, color: "var(--sub)" }}>
          <input type="checkbox" checked={!!campaign?.hero_has_text} onChange={(e) => set("hero_has_text", e.target.checked)} data-testid="poster-has-text" />
          Poster already contains text
        </label>
      )}
      <PosterBox poster={poster} style={{ height: slot === "hero" ? 160 : 120, marginBottom: 10 }}>
        {slot === "hero" && (
          <>
            <div className="hero-scrim" style={{ position: "absolute", inset: 0 }} />
            {showOverlayText ? (
              <div style={{ position: "absolute", bottom: 12, left: 14 }}>
                <div style={{ color: "#fff", fontSize: 16, fontWeight: 500 }}>{campaign?.tagline || campaign?.title || "Headline preview"}</div>
                <div style={{ color: "var(--banner-sub)", fontSize: 12 }}>{campaign?.brand_name || "Brand"}</div>
              </div>
            ) : (
              <div style={{ position: "absolute", bottom: 12, left: 14, width: 160 }}>
                <div className="progress-track"><div className="progress-fill" style={{ width: "45%" }} /></div>
                <div style={{ fontSize: 11, color: "var(--banner-sub)", marginTop: 5 }}>₹0 of budget paid out</div>
              </div>
            )}
          </>
        )}
      </PosterBox>
      <div style={{ display: "flex", gap: 8 }}>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid={`poster-input-${slot}`} />
        <button className="btn-outline focus-ring" onClick={() => fileRef.current.click()} disabled={busy} data-testid={`poster-upload-${slot}`} style={{ padding: "8px 14px", fontSize: 12 }}><Upload size={13} /> {busy ? "Uploading…" : "Upload"}</button>
        {poster && <button className="btn-outline focus-ring" onClick={remove} style={{ padding: "8px 14px", fontSize: 12, color: "var(--danger)" }}><Trash2 size={13} /> Remove</button>}
      </div>
    </div>
  );
}
